import httpClient, { handleAPIError } from './httpClient'
import { AssignmentConfiguration, ValidationError, ApiResponse, MilestoneAssignment, SubtaskConfiguration } from '../types'
import { MockAssignmentService } from '../mock'
import { API_ENDPOINTS } from '../utils/apiEndpoints';
import { WHOLE_USE_MOCK } from './api';
import planService from './planService';

export interface AssignmentValidationResponse {
  isValid: boolean
  errors: ValidationError[]
}

class AssignmentService {
  private static useMock: boolean = true;
  // Get assignment data for a plan
  async getAssignmentData(Id: string): Promise<ApiResponse<AssignmentConfiguration>> {
    try {
      // Use mock service for development
      if (AssignmentService.useMock && WHOLE_USE_MOCK) {
        return await MockAssignmentService.getAssignmentData(Id)
      }
      let res = await planService.getMilestonesByPlan(Id);
      // 转换数据结构以匹配AssignmentConfiguration接口
      const assignmentData = {
        Id,
        assignments: res.data
      };
      
      return {
        success: true,
        data: assignmentData,
        traceId: `trace_${Date.now()}`,
        flag: 'success',
        errorMessage: null
      };
    } catch (error) {
      throw handleAPIError(error)
    }
  }

  // Save assignment data (draft)
  async saveAssignmentData(Id: string, assignmentData: AssignmentConfiguration): Promise<ApiResponse<AssignmentConfiguration>> {
    try {
      // Use mock service for development
      if (AssignmentService.useMock && WHOLE_USE_MOCK) {
        const result = await MockAssignmentService.saveAssignment({ id: Id, ...assignmentData });
        if (result.success) {
          return {
            success: true,
            data: result.data,
            traceId: result.traceId || `trace_${Date.now()}`,
            flag: 'success',
            errorMessage: null
          };
        } else {
          return {
            success: false,
            data: null,
            traceId: result.traceId || `trace_${Date.now()}`,
            flag: 'fail',
            errorMessage: result.errorMessage || '保存分配数据失败'
          };
        }
      }
      
      const response = await httpClient.post<ApiResponse<AssignmentConfiguration>>(API_ENDPOINTS.PLAN.ASSIGNMENTS_SAVE, assignmentData);
      return response.data
    } catch (error) {
      throw handleAPIError(error)
    }
  }

  // Validate assignment data
  async validateAssignmentData(assignmentData: AssignmentConfiguration): Promise<ApiResponse<AssignmentValidationResponse>> {
    try {
      // Use mock service for development
      if (AssignmentService.useMock && WHOLE_USE_MOCK) {
        const result = await MockAssignmentService.validateAssignment(assignmentData);
        if (result.success) {
          return {
            success: true,
            data: result.data,
            traceId: result.traceId || `trace_${Date.now()}`,
            flag: 'success',
            errorMessage: null
          };
        } else {
          // Mock服务的validate方法通常不会返回错误，但为了类型一致性，我们这样处理
          return {
            success: false,
            data: null,
            traceId: result.traceId || `trace_${Date.now()}`,
            flag: 'fail',
            errorMessage: '验证分配数据失败'
          };
        }
      }
      
      const response = await httpClient.post<ApiResponse<AssignmentValidationResponse>>(API_ENDPOINTS.PLAN.ASSIGNMENTS_VALIDATE, assignmentData);
      return response.data
    } catch (error) {
      throw handleAPIError(error)
    }
  }

  // Submit assignment data (final)
  async submitAssignmentData(assignmentData: AssignmentConfiguration): Promise<ApiResponse<AssignmentConfiguration>> {
    try {
      // Use mock service for development
      if (AssignmentService.useMock && WHOLE_USE_MOCK) {
        const result = await MockAssignmentService.submitAssignment(assignmentData);
        if (result.success) {
          return {
            success: true,
            data: result.data,
            traceId: result.traceId || `trace_${Date.now()}`,
            flag: 'success',
            errorMessage: null
          };
        } else {
          // Mock服务的submit方法通常不会返回错误，但为了类型一致性，我们这样处理
          return {
            success: false,
            data: null,
            traceId: result.traceId || `trace_${Date.now()}`,
            flag: 'fail',
            errorMessage: '提交分配数据失败'
          };
        }
      }
      
      const response = await httpClient.post<ApiResponse<AssignmentConfiguration>>(API_ENDPOINTS.PLAN.ASSIGNMENTS_SUBMIT, assignmentData);
      return response.data
    } catch (error) {
      throw handleAPIError(error)
    }
  }

  // Get available personnel for assignment
  async getAvailablePersonnel(departmentId?: string): Promise<ApiResponse<any[]>> {
    try {
      // Use mock service for development
      if (AssignmentService.useMock && WHOLE_USE_MOCK) {
        const result = await MockAssignmentService.getAvailablePersonnel();
        // 修复Mock服务返回的数据结构问题
        if (result.success) {
          return {
            success: true,
            data: result.data,
            traceId: `trace_${Date.now()}`,
            flag: 'success',
            errorMessage: null
          };
        } else {
          return {
            success: false,
            data: null,
            traceId: `trace_${Date.now()}`,
            flag: 'fail',
            errorMessage: result.message || '获取可用人员失败'
          };
        }
      }
      
      // Uncomment below for production API calls
      // 如果提供了部门ID，则按部门查询，否则查询所有人员
      let url = API_ENDPOINTS.PLAN.AVALIABLE_PERSION;
      if (departmentId) {
        url += '?departmentId=' + encodeURIComponent(departmentId);
      }
      const response = await httpClient.get<ApiResponse<any[]>>(url);
      return response.data;
    } catch (error) {
      throw handleAPIError(error)
    }
  }

}


export const assignmentService = new AssignmentService()